import { useId, useState } from 'react';
import { cn } from '../../utils/cn';
import { Icon } from '../../icons/Icon';
import { useDsT } from '../../services/i18n';

export interface DiagnosticDetailsProps {
  /** Stable failure code, e.g. `inventory.insufficient_available`. */
  code?: string;
  /** What `code` is, when it is not a failure code — e.g. "Required permissions" on an access denial. */
  codeLabel?: string;
  /** Correlation id an operator can quote to support. */
  correlationId?: string;
  /** HTTP status. */
  status?: number;
  label?: string;
  diagnosticLabel?: string;
  copyLabel?: string;
  copiedLabel?: string;
  className?: string;
}

/**
 * The one place the product prints a diagnostic.
 *
 * An error code, an HTTP status and a correlation id are support's vocabulary, not the operator's: they explain
 * nothing to the person looking at the screen, and printed in the body they make an ordinary "we couldn't save
 * that" look like a crash. They are still needed — a support call starts with "what is the reference?" — so they
 * live here: collapsed by default, one click away, with a copy action, in identical wording everywhere.
 *
 * Extracted from `ErrorState` so the rule is enforced by having only one implementation. Any surface that shows
 * a failure — panel, callout, form summary — renders this rather than its own line of monospace.
 */
export function DiagnosticDetails({
  code,
  codeLabel: codeLabelProp,
  correlationId,
  status,
  label: labelProp,
  diagnosticLabel: diagnosticLabelProp,
  copyLabel: copyLabelProp,
  copiedLabel: copiedLabelProp,
  className,
}: DiagnosticDetailsProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const id = useId();
  const t = useDsT();
  const codeLabel = codeLabelProp ?? t('ds.errorState.code', 'Code');
  const label = labelProp ?? t('ds.errorState.technical', 'Technical details');
  const diagnosticLabel = diagnosticLabelProp ?? t('ds.errorState.diagnosticId', 'Diagnostic ID');
  const copyLabel = copyLabelProp ?? t('ds.errorState.copyDiagnosticId', 'Copy diagnostic ID');
  const copiedLabel = copiedLabelProp ?? t('ds.common.copied', 'Copied');
  if (!code && !correlationId && !status) return null;
  const copy = async () => {
    if (!correlationId) return;
    try {
      await navigator.clipboard.writeText(correlationId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard unavailable: the id stays selectable */
    }
  };
  return (
    <div className={cn('zo-error-technical text-xs', className)}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 rounded-sm text-fg-subtle transition-colors hover:text-fg-muted focus-visible:shadow-focus focus-visible:outline-none"
      >
        <Icon name="chevron-right" size={12} className={cn('transition-transform duration-[var(--zo-motion-fast)]', open && 'rotate-90')} />
        {label}
      </button>
      {open ? (
        <dl id={id} className="mt-1.5 grid grid-cols-[auto_1fr] items-start gap-x-3 gap-y-1 text-start text-fg-muted">
          {correlationId ? (
            <>
              <dt>{diagnosticLabel}</dt>
              <dd className="flex min-w-0 items-center gap-2">
                <span className="truncate font-mono text-fg select-all">{correlationId}</span>
                <button
                  type="button"
                  onClick={() => void copy()}
                  className="shrink-0 rounded-sm text-fg-subtle hover:text-fg focus-visible:shadow-focus focus-visible:outline-none"
                  aria-label={copied ? copiedLabel : copyLabel}
                  title={copied ? copiedLabel : copyLabel}
                >
                  <Icon name={copied ? 'check' : 'copy'} size={12} />
                </button>
              </dd>
            </>
          ) : null}
          {code ? (
            <>
              <dt>{codeLabel}</dt>
              <dd className="font-mono">{code}</dd>
            </>
          ) : null}
          {status ? (
            <>
              <dt>{t('ds.errorState.status', 'Status')}</dt>
              <dd className="font-mono tabular-nums">{status}</dd>
            </>
          ) : null}
        </dl>
      ) : null}
    </div>
  );
}
